import { type FC } from 'react';

import { EsgCategory } from '@/api/esg/models/EsgCategory';
import { EsgCurrencyCategory } from '@/api/esg/models/EsgCurrencyCategory';
import { Show } from '@/components/Show';
import { useTexts } from '@/hooks/useTexts';
import { CurrencyCode } from '@/types/CurrencyCode';

import { EsgValueListItem } from './EsgValueListItem';

interface Props {
  solutionCompanies: EsgCategory;
  euTaxonomyAlignment: EsgCategory;
  euTaxonomyEligibility: EsgCategory;
  carbonIntensities: EsgCurrencyCategory[];
  pai11GhgEmissionsSum: EsgCategory | undefined;
}

export const EsgOverviewValueList: FC<Props> = ({
  euTaxonomyAlignment,
  euTaxonomyEligibility,
  carbonIntensities,
  pai11GhgEmissionsSum,
}) => {
  const fms = useTexts();

  const texts = fms.pages.esg.overview;

  const carbonFootprintData = carbonIntensities.find(
    (score) => score.currency === CurrencyCode.NOK,
  );

  const carbonFootprintDifference = carbonFootprintData?.difference ?? 0;

  const emissionsText =
    carbonFootprintDifference <= 0
      ? texts.lowerEmissions
      : texts.higherEmissions;

  return (
    <dl>
      {carbonFootprintData?.benchmark && (
        <EsgValueListItem
          description={texts.carbonFootprint}
          value={carbonFootprintData?.difference}
          unit={`% ${emissionsText}`}
        />
      )}

      {!carbonFootprintData?.benchmark && pai11GhgEmissionsSum && (
        <EsgValueListItem
          description={texts.carbonFootprint}
          value={pai11GhgEmissionsSum.kpi.value}
          // Add a space between the value and the unit.
          unit={` ${fms.pages.esg.carbonFootprint.tonnage}`}
        />
      )}

      {/** @todo: Temporary taxonomy data need to be hidden, see SAMIK-1799 */}
      <Show when={false}>
        <EsgValueListItem
          description={texts.taxonomyEligibility}
          value={euTaxonomyEligibility?.kpi?.value}
          unit="%"
        />
        <EsgValueListItem
          description={texts.taxonomyAlignment}
          value={euTaxonomyAlignment?.kpi?.value}
          unit="%"
        />
      </Show>
    </dl>
  );
};
